import type {
  ICs2Hud,
  TCs2CrosshairMode,
  TCs2TopPanel,
} from "MVVM/@types/user";

// Where each supported convar lands, and the range it has to fall in. Only
// appearance settings that still apply while spectating a demo: anything tied to
// the local player's POV (radar centering, radar rotation) draws nothing in a
// replay. Mirrors the ranges in beatles
// (app/models/concerns/has_cs2_clip_config.rb), which is what actually enforces
// them. Parsing here is only so we can preview an import.
const HUD_RANGES: Record<string, [keyof ICs2Hud, number, number]> = {
  cl_hud_radar_scale: ["radarSize", 0.8, 1.3],
  cl_radar_scale: ["mapZoom", 0.25, 1.0],
  hud_scaling: ["hudScale", 0.5, 0.95],
};

// Covers both the quoted pairs in a .vcfg and the bare `name value` lines in an
// autoexec. The value has to be a plain number or a bare boolean, so a line
// carrying a second command can never parse. CS2 writes booleans as true/false
// rather than 1/0, which we normalise to numbers.
const LINE = /^\s*"?([a-z_0-9.]+)"?\s+"?(-?\d+(?:\.\d+)?|true|false)"?\s*$/i;

// cs2_video.txt rather than a convar file: the resolution the player actually
// runs the game at, which is what a clip should be rendered to match.
const VIDEO_WIDTH = "setting.defaultres";
const VIDEO_HEIGHT = "setting.defaultresheight";

const HUD_COLOR = "cl_hud_color";
const TOP_PANEL = "cl_teamcounter_playercount_instead_of_avatars";

// A config with any crosshair convar in it means the player has set one up, so
// their clips should use it. PLAYER draws whatever the demo recorded.
const CROSSHAIR_CONVAR = /^cl_crosshair/;

export interface IParsedCs2Config {
  hud: Partial<ICs2Hud>;
  /** Game resolution from cs2_video.txt, for matching a clip quality config. */
  resolution?: { width: number; height: number };
  crosshair?: TCs2CrosshairMode;
  /** The lines we recognised, for showing back to the user. */
  matched: string[];
}

const clampColor = (value: number) =>
  Math.min(10, Math.max(0, Math.round(value)));

export const parseCs2Config = (text: string): IParsedCs2Config => {
  const found = new Map<string, number>();

  text.split(/\r?\n/).forEach((line) => {
    const match = LINE.exec(line);
    if (!match) return;

    const raw = match[2].toLowerCase();
    const value = raw === "true" ? 1 : raw === "false" ? 0 : Number(raw);
    if (!Number.isFinite(value)) return;

    // Last one wins, the way the game reads a config top to bottom.
    found.set(match[1].toLowerCase(), value);
  });

  const hud: Partial<ICs2Hud> = {};
  const matched: string[] = [];

  const color = found.get(HUD_COLOR);
  if (color !== undefined) {
    hud.color = clampColor(color);
    matched.push(`${HUD_COLOR} ${hud.color}`);
  }

  const topPanel = found.get(TOP_PANEL);
  if (topPanel !== undefined) {
    hud.topPanel = (topPanel >= 1 ? "COLLAPSED" : "EXPANDED") as TCs2TopPanel;
    matched.push(`${TOP_PANEL} ${topPanel}`);
  }

  Object.entries(HUD_RANGES).forEach(([convar, [field, min, max]]) => {
    const value = found.get(convar);
    if (value === undefined || value < min || value > max) return;

    hud[field] = value as never;
    matched.push(`${convar} ${value}`);
  });

  const width = found.get(VIDEO_WIDTH);
  const height = found.get(VIDEO_HEIGHT);
  const resolution =
    width && height && width > 0 && height > 0 ? { width, height } : undefined;

  const hasCrosshair = [...found.keys()].some((name) =>
    CROSSHAIR_CONVAR.test(name),
  );

  return {
    hud,
    matched,
    ...(resolution ? { resolution } : {}),
    ...(hasCrosshair ? { crosshair: "PLAYER" } : {}),
  };
};

// How many settings an import would actually change. Drives both the summary
// shown to the user and the decision to leave things alone entirely.
export const countImportedSettings = (parsed: IParsedCs2Config): number =>
  Object.keys(parsed.hud).length +
  (parsed.crosshair ? 1 : 0) +
  (parsed.resolution ? 1 : 0);
